import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import {
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	RouteConfigLoadEnd,
	RouteConfigLoadStart,
	Router,
	Event as RouterEvent,
	RouterOutlet
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SidenavService } from './_services/side-nav.service';
import { BookingService } from './services/booking.service';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import {
	LollySpinnerLoaderComponent,
	SecondaryButtonComponent
} from 'components';
import { ToastV2Component } from './toast-v2/components/toast-v2/toast-v2.component';
import { ToastNotificationService } from './shared/toast-notification/toast-notification.service';
import { ToastV2TypeEnum } from './toast-v2/models/toast-v2.model';

/**
 * Matches the ESM dynamic-import failure this app's build actually produces
 * for a stale lazy chunk. `@angular-devkit/build-angular:application`
 * (`angular.json`) is esbuild + native `import()`, not webpack, so a chunk
 * that a redeploy removed throws a `TypeError` from this message family — or,
 * when the old URL now resolves to the SPA's own `index.html`, a MIME-type
 * refusal. Webpack's `ChunkLoadError` name is matched defensively below in
 * case the build configuration ever changes; this app should never produce
 * it today. This is a browser string, not a public API — if the wording
 * drifts, the classifier just degrades to the generic failure message
 * instead of breaking (design.md, Decision 4).
 */
const STALE_BUNDLE_ERROR_PATTERN =
	/failed to fetch dynamically imported module|failed to load module script|error loading dynamically imported module/i;

function isStaleBundleFailure(error: unknown): boolean {
	if (error && (error as { name?: string }).name === 'ChunkLoadError') {
		return true;
	}
	const message =
		error instanceof Error ? error.message : String(error ?? '');
	return STALE_BUNDLE_ERROR_PATTERN.test(message);
}

@Component({
	standalone: true,
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [
		RouterOutlet,
		NgIf,
		AsyncPipe,
		MatTooltipModule,
		LollySpinnerLoaderComponent,
		ToastV2Component,
		SecondaryButtonComponent
	]
})
export class AppComponent implements OnInit {
	/**
	 * Whether the shell's rail is collapsed, so the Book a Call widget can say
	 * in a tooltip what it stops saying in words at that width.
	 */
	public railCollapsed$!: Observable<boolean>;

	@ViewChild('sidenav')
	public sidenav: MatSidenav;

	public asyncLoadCount: number;

	public isShowingRouteLoadIndicator = false;
	public showCalendlyWidget = true;

	constructor(
		private router: Router,
		private sidenavService: SidenavService,
		private translateService: TranslateService,
		private bookingService: BookingService,
		private toastNotificationService: ToastNotificationService
	) {
		// Assigned here rather than as a field initializer: with
		// useDefineForClassFields the parameter property would not exist yet,
		// and this repo only has it off — a setting nobody would think to check
		// while flipping it.
		this.railCollapsed$ = this.sidenavService.railCollapsed$;
		this.asyncLoadCount = 0;
		this.showCalendlyWidget =
			!this.isReportExportRoute(window.location.pathname) &&
			!this.isReportShareRoute(window.location.pathname);
		this.router.events.subscribe((event: RouterEvent) => {
			if (event instanceof RouteConfigLoadStart) {
				this.asyncLoadCount++;
			} else if (event instanceof RouteConfigLoadEnd) {
				this.asyncLoadCount--;
			} else if (
				event instanceof NavigationError ||
				event instanceof NavigationCancel
			) {
				// A lazy chunk that failed or a navigation that was abandoned
				// never emits `RouteConfigLoadEnd`, so the counter is released
				// here too — this is the half that must not depend on
				// classifying the failure (design.md, Risks). Clamped so it
				// can never go negative, whether several navigations fail in
				// one session or `RouteConfigLoadEnd` still fires for the same
				// one.
				this.asyncLoadCount = Math.max(0, this.asyncLoadCount - 1);
			}

			this.isShowingRouteLoadIndicator = !!this.asyncLoadCount;

			if (event instanceof NavigationEnd) {
				this.showCalendlyWidget =
					!this.isReportExportRoute(event.urlAfterRedirects) &&
					!this.isReportShareRoute(event.urlAfterRedirects);
			}

			if (event instanceof NavigationError) {
				this.handleNavigationError(event);
			}
		});
	}

	/**
	 * The app's only reaction to a failed navigation (design.md, Decision 1).
	 * The person is never moved by this — Angular already restores the
	 * browser URL to wherever they were (or, on a first load, `/`, which has
	 * no "where they were" to restore) — so this only ever raises a message.
	 */
	private handleNavigationError(event: NavigationError): void {
		if (isStaleBundleFailure(event.error)) {
			this.toastNotificationService.sendToast(
				ToastV2TypeEnum.INFO,
				'A new version of Lolly is available. Reload to pick it up.',
				{
					duration: 0,
					title: 'Update available',
					action: {
						label: 'Reload',
						run: () => {
							// A full reload, not a router navigation — the
							// point is fetching new code, and it lands on the
							// address the person actually asked for rather
							// than the app's home page (design.md, Decision 5).
							window.location.href = event.url;
						}
					}
				}
			);
			return;
		}

		this.toastNotificationService.sendToast(
			ToastV2TypeEnum.ERROR,
			'We could not open that page. Please try again.',
			{
				title: 'Something went wrong',
				action: {
					label: 'Retry',
					run: () => {
						this.router.navigateByUrl(event.url);
					}
				}
			}
		);
	}

	public ngOnInit(): void {
		this.sidenavService.setSidenav(this.sidenav);
		this.translateService.addLangs(['en', 'fr']);
		this.translateService.setDefaultLang('en');
		const browserLanguage = this.translateService.getBrowserLang();
		this.translateService.use(
			browserLanguage.match(/en|fr/) ? browserLanguage : 'en'
		);
	}

	bookADemo() {
		this.bookingService.bookDemo();
	}

	private isReportExportRoute(url: string): boolean {
		const [path] = url.split(/[?#]/);
		return path.endsWith('/export');
	}

	/**
	 * A report opened from its view-only link is read by people outside the
	 * account — leadership, a client — and a "Book a Call" face in its corner
	 * would be Lolly's sales pitch on somebody else's document.
	 */
	private isReportShareRoute(url: string): boolean {
		const [path] = url.split(/[?#]/);
		return path.startsWith('/social-listening/reports/shared/');
	}
}
